import { omit } from 'lodash-es'
import {
  createDefaultLayer,
  DefaultLayerId,
  type ICADMetadata,
  type IPlaneMetaData,
} from './module/CADModule/manager/DataManager'
import { ColorMethod, EntityType, LineType } from './module/CADModule/model/common/constant'

const DEFAULT_POINT_2D = { x: 0, y: 0 }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isColorMethod(value: unknown): value is ColorMethod {
  return (
    value === ColorMethod.BY_LAYER ||
    value === ColorMethod.BY_BLOCK ||
    value === ColorMethod.INDEX ||
    value === ColorMethod.RGB
  )
}

function isLineType(value: unknown): value is LineType {
  return Object.values(LineType).includes(value as LineType)
}

function isEntityType(value: unknown): value is EntityType {
  return Object.values(EntityType).includes(value as EntityType)
}

function toNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function toBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function toNonEmptyString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

function toPoint2D(value: unknown, fallback = DEFAULT_POINT_2D) {
  if (!isRecord(value)) {
    return { ...fallback }
  }
  return {
    x: toNumber(value.x, fallback.x),
    y: toNumber(value.y, fallback.y),
  }
}

function toPoint3D(value: unknown, fallback: { x: number; y: number; z: number }) {
  if (!isRecord(value)) {
    return { ...fallback }
  }
  return {
    x: toNumber(value.x, fallback.x),
    y: toNumber(value.y, fallback.y),
    z: toNumber(value.z, fallback.z),
  }
}

function _toPointList(value: unknown) {
  if (!Array.isArray(value)) {
    return []
  }
  return value.filter(isRecord).map((point) => toPoint2D(point))
}

function _toNumberList(value: unknown) {
  if (!Array.isArray(value)) {
    return []
  }
  return value.filter((item): item is number => typeof item === 'number' && Number.isFinite(item))
}

function toEntityType(value: unknown): EntityType | null {
  if (value === EntityType.Text) {
    return EntityType.MText
  }
  return isEntityType(value) ? value : null
}

/**
 * 创建默认的 CAD 绘图平面元数据，表示世界坐标系下的 XY 平面。
 *
 * @returns 使用世界坐标原点、Z 轴法线、Y 轴上方向的平面元数据
 */
export function createDefaultCadPlaneMetadata(): IPlaneMetaData {
  return {
    origin: { x: 0, y: 0, z: 0 },
    normal: { x: 0, y: 0, z: 1 },
    up: { x: 0, y: 1, z: 0 },
    elevation: 0,
  }
}

function normalizePlaneMetadata(plane: unknown): IPlaneMetaData {
  const fallback = createDefaultCadPlaneMetadata()
  if (!isRecord(plane)) {
    return fallback
  }
  return {
    origin: toPoint3D(plane.origin, fallback.origin),
    normal: toPoint3D(plane.normal, fallback.normal),
    up: toPoint3D(plane.up, fallback.up),
    elevation: toNumber(plane.elevation, fallback.elevation),
  }
}

function normalizeLayerData(layer: unknown, index: number) {
  const fallbackLayer = createDefaultLayer().serialize()
  const layerRecord = isRecord(layer) ? layer : {}
  const id = toNonEmptyString(
    layerRecord.id,
    index === 0 ? DefaultLayerId : `legacy-layer-${index + 1}`
  )
  return {
    id,
    name: toNonEmptyString(layerRecord.name, id === DefaultLayerId ? fallbackLayer.name : id),
    colorMethod: isColorMethod(layerRecord.colorMethod)
      ? layerRecord.colorMethod
      : fallbackLayer.colorMethod,
    color: toNumber(layerRecord.color, fallbackLayer.color),
    lineType: isLineType(layerRecord.lineType) ? layerRecord.lineType : fallbackLayer.lineType,
    lineWidth: toNumber(layerRecord.lineWidth, fallbackLayer.lineWidth),
    visible: toBoolean(layerRecord.visible, fallbackLayer.visible),
    locked: toBoolean(layerRecord.locked, fallbackLayer.locked),
  }
}

function normalizeEntityData(entity: unknown, index: number) {
  if (!isRecord(entity)) {
    return null
  }

  const type = toEntityType(entity.type)
  if (!type) {
    return null
  }

  return {
    id: toNonEmptyString(entity.id, `legacy-entity-${index + 1}`),
    type,
    layerId: typeof entity.layerId === 'string' ? entity.layerId : '',
    colorMethod: isColorMethod(entity.colorMethod) ? entity.colorMethod : ColorMethod.BY_LAYER,
    color: toNumber(entity.color, 0xffffff),
    lineType: isLineType(entity.lineType) ? entity.lineType : LineType.Solid,
    lineWidth: toNumber(entity.lineWidth, 0.25),
    ...omit(entity, ['id', 'type', 'layerId', 'colorMethod', 'color', 'lineType', 'lineWidth']),
  }
}

/**
 * 将外部 JSON 数据标准化为 ICADMetadata，对字段类型进行校验与缺省值填充。
 *
 * 会自动补充默认图层、修正缺失的 layerId，以及规范化绘图平面。
 *
 * @param data - 外部 JSON 数据，需为对象
 * @returns 标准化后的 CAD 元数据
 */
export function normalizeJsonCadMetadata(data: unknown): ICADMetadata {
  if (!isRecord(data)) {
    throw new Error('JsonCadLoader: JSON 数据必须是对象')
  }

  const inputLayers = Array.isArray(data.layers) ? data.layers : []
  const inputEntities = Array.isArray(data.entities) ? data.entities : []

  const layers = inputLayers.map((layer, index) => normalizeLayerData(layer, index))
  const entities = inputEntities
    .map((entity, index) => normalizeEntityData(entity, index))
    .filter((entity): entity is NonNullable<typeof entity> => entity !== null)

  if (entities.length > 0 && layers.length === 0) {
    layers.push(createDefaultLayer().serialize())
  }

  const fallbackLayerId = layers[0]?.id ?? DefaultLayerId
  const knownLayerIds = new Set(layers.map((layer) => layer.id))
  for (const entity of entities) {
    if (!entity.layerId || !knownLayerIds.has(entity.layerId)) {
      entity.layerId = fallbackLayerId
    }
  }

  return {
    layers,
    entities,
    plane: normalizePlaneMetadata(data.plane),
  }
}
